<template>
  <div class="routing-guide flex flex-column flex-gap-1">
    <h3>{{ t('common.preferred') }}</h3>
    <RateShopTable
      :id="`${id}-preferred-rates`"
      :rate-quotes="filteredPreferredRates"
      :editable="editable"
      :loading="isPolling"
      :selectable="!disabled && rateable"
      @edit-spot-quote="emit('edit-spot-quote', $event)"
      @edit-quote="emit('edit-quote', $event)"
      @rate-selected="emit('rate-selected', $event)"
      @send-to-brokerage="emit('send-to-brokerage', $event)"
    >
      <template #empty>
        {{
          t(
            serviceTypeFilterSet && preferredRates.length
              ? 'routingGuides.noRatesForParamAtSelectedLevel'
              : 'routingGuides.noRatesForParam',
            { param: t('routingGuides.preferredCarriers', 2) },
          )
        }}
      </template>
    </RateShopTable>

    <h3>{{ t('common.additional') }}</h3>
    <RateShopTable
      :id="`${id}-additional-rates`"
      :rate-quotes="filteredStandardRates"
      :editable="editable"
      :loading="isPolling"
      :selectable="!disabled && rateable"
      price-class="text-normal"
      @edit-spot-quote="emit('edit-spot-quote', $event)"
      @edit-quote="emit('edit-quote', $event)"
      @rate-selected="emit('rate-selected', $event)"
      @send-to-brokerage="emit('send-to-brokerage', $event)"
    >
      <template #empty>
        {{
          serviceTypeFilterSet && standardRates.length
            ? t('routingGuides.noAdditionalRatesAtSelectedLevel')
            : t('common.noParam', {
                param: t('rateShop.additionalRates', 2),
              })
        }}
      </template>
    </RateShopTable>

    <template
      v-if="
        excludedRates.length &&
        serviceTypeFilters[WellKnownRateStrategy.EXCLUDED]
      "
    >
      <h3>{{ t('rateShop.excluded') }}</h3>
      <RateShopTable
        :id="`${id}-excluded-rates`"
        :rate-quotes="filteredExcludedRates"
        :loading="isPolling"
        price-class="text-danger font-medium"
      >
        <template #empty>
          {{
            t('routingGuides.noRatesForParamAtSelectedLevel', {
              param: t('routingGuides.excludedCarriers', 2),
            })
          }}
        </template>
      </RateShopTable>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import { useRoutingGuide } from '@/composables';
import {
  RateQuote,
  RateShop,
  RateShopStatus,
  type ServiceTypeFilters,
  WellKnownRateStrategy,
} from '@/model';

import RateShopTable from '@/components/rateShop/RateShopTable.vue';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('routing-guide').uid,
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  serviceTypeFilters: {
    type: Object as PropType<ServiceTypeFilters>,
    default: () => ({}),
  },
  /**
   * Specifies if this instance of this component allows editing the rate
   *
   * Editability rules (permissions, rate source, parent entity status, etc) are
   * handled in `RateShopTableSource`
   */
  editable: {
    type: Boolean,
    default: () => false,
  },
  rateable: {
    type: Boolean,
    default: () => false,
  },
  serviceTypeFilterSet: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  rateShop,
  serviceTypeFilters,
  editable,
  rateable,
  serviceTypeFilterSet,
  disabled,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'edit-spot-quote', spotQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'send-to-brokerage', rateQuote: RateQuote): void;
}>();

const { t } = useI18n();
const {
  excludedRates,
  filteredExcludedRates,
  filteredPreferredRates,
  filteredStandardRates,
  preferredRates,
  standardRates,
} = useRoutingGuide(rateShop, serviceTypeFilters);

const isPolling = computed(() => {
  return (
    rateShop.value.loading ||
    (rateShop.value.data?.status !== RateShopStatus.ERROR &&
      rateShop.value.data?.status !== RateShopStatus.COMPLETED)
  );
});
</script>

<style>
.routing-guide {
  h3 {
    font-size: 14px;
    font-weight: 500;
    margin-top: var(--sp-3);
  }
}
</style>
